import type { z } from 'zod'

/**
 * Amarração em compilação entre os schemas zod (validação em runtime) e os tipos gerados do
 * OpenAPI do backend (`openapi.gen.ts`, via `npm run api:sync`). NR-009.
 *
 * Uso: `export const xSchema = responseSchema<Schemas['X']>()(z.object({...}))`.
 * Se o schema divergir do contrato, `npm run typecheck` falha na chamada e a mensagem lista
 * os caminhos com problema.
 *
 * O que é checado (recursivo: objetos aninhados, arrays, records e campos nullable/opcionais):
 * - nomes de campo idênticos nos dois sentidos: campo a mais no zod **ou** campo do contrato
 *   ausente no zod quebra o typecheck (atribuibilidade estrutural sozinha não pega nenhum dos
 *   dois quando o campo é opcional, que foi o caso `argo_workflow_url` × `workflow_url`);
 * - a saída do zod (`z.output`) é atribuível ao tipo do contrato (obrigatoriedade, null, tipos);
 * - só em respostas: todo payload válido pelo contrato é aceito pela entrada do zod (`z.input`),
 *   ignorando só o estreitamento de literais (enum/regex no FE são refinamentos permitidos).
 *   Assim um campo que o contrato declara opcional/nullable não pode ser obrigatório no zod.
 */

type Defined<T> = Exclude<T, null | undefined>

type At<P extends string> = P extends '' ? '(raiz)' : P

type Join<P extends string, K> = P extends '' ? `${K & string}` : `${P}.${K & string}`

type IsArray<T> = [T] extends [readonly unknown[]] ? true : false
/** Folha = sem nenhum membro objeto (primitivos, literais, `unknown`). */
type IsLeaf<T> = [Extract<T, object>] extends [never] ? true : false
type HasIndex<T> = string extends keyof T ? true : false

/** Caminhos onde os nomes de campo divergem; `never` quando os formatos batem. */
export type ShapeIssues<Zod, Contract, P extends string = ''> = ShapeIssuesDefined<
  Defined<Zod>,
  Defined<Contract>,
  P
>

type ShapeIssuesDefined<A, B, P extends string> =
  IsLeaf<A> extends true
    ? IsLeaf<B> extends true
      ? never
      : `${At<P>}: folha no zod, objeto/array no contrato`
    : IsLeaf<B> extends true
      ? `${At<P>}: objeto/array no zod, folha no contrato`
      : IsArray<A> extends true
        ? IsArray<B> extends true
          ? ShapeIssues<ElementOf<A>, ElementOf<B>, `${P}[]`>
          : `${At<P>}: array no zod, objeto no contrato`
        : IsArray<B> extends true
          ? `${At<P>}: objeto no zod, array no contrato`
          : ObjectIssues<A, B, P>

type ElementOf<T> = T extends readonly (infer E)[] ? E : never

type ObjectIssues<A, B, P extends string> =
  HasIndex<B> extends true
    ? // Record no contrato: o zod pode restringir as chaves (ex.: JobKey); os valores precisam bater.
      ShapeIssues<A[keyof A], B[keyof B], `${P}{}`>
    : HasIndex<A> extends true
      ? `${At<P>}: record no zod, objeto com campos fixos no contrato`
      : | { [K in Exclude<keyof A, keyof B>]: `${Join<P, K>}: campo a mais no zod` }[Exclude<
            keyof A,
            keyof B
          >]
        | {
            [K in Exclude<keyof B, keyof A>]: `${Join<P, K>}: campo do contrato ausente no zod`
          }[Exclude<keyof B, keyof A>]
        | { [K in keyof A & keyof B]-?: ShapeIssues<A[K], B[K], Join<P, K>> }[keyof A & keyof B]

/** Literais viram o tipo primitivo; estrutura, opcionalidade e null são preservados. */
type WidenLiterals<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T extends readonly (infer E)[]
        ? WidenLiterals<E>[]
        : T extends object
          ? { [K in keyof T]: WidenLiterals<T[K]> }
          : T

type OutputIssue<Zod extends z.ZodTypeAny, Contract> = [z.output<Zod>] extends [Contract]
  ? never
  : 'z.output não é atribuível ao contrato (obrigatoriedade, null ou tipo de campo)'

type InputIssue<Zod extends z.ZodTypeAny, Contract> = [Contract] extends [
  WidenLiterals<z.input<Zod>>,
]
  ? never
  : 'o contrato aceita payloads que a entrada do zod recusa (campo opcional/nullable no contrato e obrigatório no zod?)'

export type RequestIssues<Zod extends z.ZodTypeAny, Contract> =
  ShapeIssues<z.output<Zod>, Contract> | OutputIssue<Zod, Contract>

export type ResponseIssues<Zod extends z.ZodTypeAny, Contract> =
  RequestIssues<Zod, Contract> | InputIssue<Zod, Contract>

/** `true` quando não há divergência; senão, o objeto de erro com os problemas encontrados. */
type Verdict<Issues> = [Issues] extends [never]
  ? true
  : { 'schema zod diverge do contrato': Issues }

export type ResponseMatches<Zod extends z.ZodTypeAny, Contract> = Verdict<
  ResponseIssues<Zod, Contract>
>
export type RequestMatches<Zod extends z.ZodTypeAny, Contract> = Verdict<
  RequestIssues<Zod, Contract>
>

type Guard<V> = V extends true ? unknown : V

/** Schema de resposta (o FE valida o que o backend devolve). */
export const responseSchema =
  <Contract>() =>
  <Zod extends z.ZodTypeAny>(schema: Zod & Guard<ResponseMatches<Zod, Contract>>): Zod =>
    schema

/** Schema de corpo de requisição (o FE monta e envia). */
export const requestSchema =
  <Contract>() =>
  <Zod extends z.ZodTypeAny>(schema: Zod & Guard<RequestMatches<Zod, Contract>>): Zod =>
    schema

/** Para tipos TS escritos à mão (sem zod) que viram corpo de requisição. */
export type TypeMatches<T, Contract> = Verdict<
  ShapeIssues<T, Contract> | ([T] extends [Contract] ? never : 'tipo não é atribuível ao contrato')
>

/** Igualdade exata de uniões de literais (enum): membro a mais ou a menos quebra o typecheck. */
export type EnumMatches<Values, Contract> = Verdict<
  | ([Exclude<Values, Contract>] extends [never]
      ? never
      : `valor a mais no FE: ${Exclude<Values, Contract> & string}`)
  | ([Exclude<Contract, Values>] extends [never]
      ? never
      : `valor do contrato ausente no FE: ${Exclude<Contract, Values> & string}`)
>

/** Enum zod com exatamente os valores do contrato (um valor novo no backend quebra o typecheck). */
export const enumSchema =
  <Contract>() =>
  <Zod extends z.ZodTypeAny>(schema: Zod & Guard<EnumMatches<z.output<Zod>, Contract>>): Zod =>
    schema
